import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Tag, ChevronDown } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import TinyMCEEditor from './TinyMCEEditor';
import ProductLinksEditor from './ProductLinksEditor';

const ContextModal = ({ context, clientId, onClose, onSuccess, isClientMode = false }) => {
  const isEdit = !!context;
  const [existingCategories, setExistingCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categoryInput, setCategoryInput] = useState('');
  const [contentValue, setContentValue] = useState('');
  const [productLinks, setProductLinks] = useState([]);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm();

  const tagsValue = watch('tags', '');

  useEffect(() => {
    // Fetch existing categories
    const fetchCategories = async () => {
      try {
        if (isClientMode) {
          // For client mode, we'll use a simpler set of categories
          setExistingCategories(['General', 'Products', 'Services', 'Support', 'FAQ', 'Apie mus']);
        } else {
          const response = await axios.get(`/context/client/${clientId}`);
          setExistingCategories(response.data.categories || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    if (!isClientMode || clientId) {
      fetchCategories();
    }

    if (context) {
      setValue('title', context.title);
      setContentValue(context.content);
      setValue('category', context.category);
      setCategoryInput(context.category);
      setValue('tags', context.tags?.join(', ') || '');
      setValue('priority', context.priority || 0);
      setValue('isActive', context.isActive !== false);
      setProductLinks(context.productLinks || []);
    } else {
      setCategoryInput('General');
      setValue('category', 'General');
      setContentValue('');
      setProductLinks([]);
    }
  }, [context, setValue, clientId]);

  const handleCategorySelect = (category) => {
    setCategoryInput(category);
    setValue('category', category);
    setShowCategoryDropdown(false);
  };

  const handleCategoryInputChange = (e) => {
    const value = e.target.value;
    setCategoryInput(value);
    setValue('category', value);
    setShowCategoryDropdown(value.length > 0);
  };

  const filteredCategories = existingCategories.filter(cat =>
    cat.toLowerCase().includes(categoryInput.toLowerCase()) && 
    cat.toLowerCase() !== categoryInput.toLowerCase()
  );

  const onSubmit = async (data) => {
    try {
      if (!contentValue.trim()) {
        toast.error('Content is required');
        return;
      }

      const payload = {
        title: data.title,
        content: contentValue,
        category: categoryInput || 'General',
        tags: data.tags ? data.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        priority: parseInt(data.priority) || 0,
        isActive: data.isActive,
        productLinks: productLinks.filter(link => link.title && link.url)
      };

      // Add clientId only for admin mode
      if (!isClientMode) {
        payload.clientId = clientId;
      }

      if (isEdit) {
        const endpoint = isClientMode ? `/context/my-contexts/${context._id}` : `/context/${context._id}`;
        await axios.put(endpoint, payload);
        toast.success('Knowledge item updated successfully');
      } else {
        const endpoint = isClientMode ? '/context/my-contexts' : '/context';
        await axios.post(endpoint, payload);
        toast.success('Knowledge item created successfully');
      }
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {isEdit ? 
              (isClientMode ? 'Edit Knowledge Item' : 'Edit Context') : 
              (isClientMode ? 'Add New Knowledge Item' : 'Add New Context')
            }
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter context title..."
              {...register('title', { required: 'Title is required' })}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-md">
              <TinyMCEEditor
                value={contentValue}
                onChange={setContentValue}
                placeholder="Enter the context content that the chatbot should know about..."
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Content with HTML formatting support. This will be used by the AI to answer user questions.
            </p>
            <div className="mt-2 text-xs text-gray-400">
              <strong>Tip:</strong> Use formatting to organize information clearly - 
              <strong>bold</strong> for key points, <em>lists</em> for structured data, 
              <strong>links</strong> for references.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., FAQ, Products, Support"
                  value={categoryInput}
                  onChange={handleCategoryInputChange}
                  onFocus={() => setShowCategoryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                />
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                
                {showCategoryDropdown && filteredCategories.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredCategories.map((category) => (
                      <div
                        key={category}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                        onMouseDown={() => handleCategorySelect(category)}
                      >
                        {category}
                      </div>
                    ))}
                  </div>
                )}
                
                {showCategoryDropdown && existingCategories.length > 0 && filteredCategories.length === 0 && categoryInput && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Press Enter to create "{categoryInput}"
                    </div>
                  </div>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Type to search existing categories or create a new one
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority (0-10)
              </label>
              <input
                type="number"
                min="0"
                max="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                {...register('priority')}
                defaultValue={0}
              />
              <p className="mt-1 text-sm text-gray-500">
                Higher priority content is more likely to be used
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter tags separated by commas..."
                {...register('tags')}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Separate multiple tags with commas (e.g., pricing, features, support)
            </p>
          </div>

          <ProductLinksEditor
            productLinks={productLinks}
            onChange={setProductLinks}
          />

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register('isActive')}
                defaultChecked={true}
              />
              <span className="ml-2 text-sm text-gray-700">
                Active (context will be used by the chatbot)
              </span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContextModal;